import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { 
  Building2, 
  CheckCircle, 
  ArrowRight,
  Users,
  TrendingUp,
  Shield,
  Clock,
  DollarSign,
  FileText
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_small-biz-loans/artifacts/ojg09jbh_logo.png";

const WholesaleLending = () => {
  const features = [
    {
      icon: Users,
      title: "MFI Support",
      description: "Designed specifically to support small registered microfinance institutions"
    },
    {
      icon: TrendingUp,
      title: "Productive Lending",
      description: "Enable your institution to expand productive lending to underserved communities"
    },
    {
      icon: Shield,
      title: "Institutional Growth",
      description: "Scale your lending operations with reliable wholesale funding"
    },
    {
      icon: Clock,
      title: "Flexible Terms",
      description: "6-36 month terms tailored to your institution's needs"
    }
  ];

  const eligibility = [
    "Registered microfinance institution",
    "Minimum 2 years operational history",
    "Active loan portfolio",
    "Proper licensing and compliance",
    "Demonstrated repayment capacity"
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="bg-white rounded-lg px-2 py-1 shadow-sm border border-gray-100">
                <img src={LOGO_URL} alt="Tzur Finances" className="h-8 object-contain" />
              </div>
            </Link>
            
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/products" className="text-gray-600 hover:text-[#0A2540] transition-colors font-medium">
                Business Loans
              </Link>
              <Link to="/wholesale-lending" className="text-[#0A2540] font-medium">
                Microfinance Wholesale Loans
              </Link>
              <Link to="/individual-loans" className="text-gray-600 hover:text-[#0A2540] transition-colors font-medium">
                Individual Loans
              </Link>
              <Link to="/device-finance" className="text-gray-600 hover:text-[#0A2540] transition-colors font-medium">
                Device Finance
              </Link>
            </nav>
            
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="text-[#0A2540] hover:bg-gray-100">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0A2540] to-[#1e3a8a] py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-6">
            <Building2 className="h-5 w-5 text-amber-400" />
            <span className="text-white text-sm font-medium">Microfinance Wholesale Lending</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Empowering Microfinance Institutions
          </h1>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Loans to support small registered microfinance institutions to support productive lending
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/wholesale-lending/apply">
              <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white px-8">
                Apply Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/knowledge">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                Learn More
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Key Stats */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-3xl font-bold text-[#0A2540]">$25K - $100K</p>
              <p className="text-gray-600 text-sm mt-1">Loan Amount</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[#0A2540]">3.5% - 5%</p>
              <p className="text-gray-600 text-sm mt-1">Monthly Rate</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[#0A2540]">6 - 36</p>
              <p className="text-gray-600 text-sm mt-1">Months Term</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-[#0A2540]">48hrs</p>
              <p className="text-gray-600 text-sm mt-1">Decision Time</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#0A2540] mb-4">Why Choose Our Wholesale Lending?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We understand the unique needs of microfinance institutions and provide tailored solutions
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="bg-white hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="h-12 w-12 rounded-lg bg-[#0A2540]/10 flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-[#0A2540]" />
                  </div>
                  <CardTitle className="text-lg">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription>{feature.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Eligibility */}
      <section className="py-16 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#0A2540] mb-6">Eligibility Requirements</h2>
              <p className="text-gray-600 mb-8">
                Our wholesale lending program is designed for established microfinance institutions 
                looking to expand their lending capacity.
              </p>
              <ul className="space-y-4">
                {eligibility.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-xl font-semibold text-[#0A2540] mb-6">Ready to Apply?</h3>
              <div className="space-y-4 mb-6">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-gray-400" />
                  <span className="text-gray-600">MFI registration documents</span>
                </div>
                <div className="flex items-center gap-3">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                  <span className="text-gray-600">Financial statements (2 years)</span>
                </div>
                <div className="flex items-center gap-3">
                  <Users className="h-5 w-5 text-gray-400" />
                  <span className="text-gray-600">Portfolio performance reports</span>
                </div>
              </div>
              <Link to="/wholesale-lending/apply">
                <Button className="w-full bg-[#0A2540] hover:bg-[#0A2540]/90">
                  Start Application
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-gray-900 text-gray-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm">Tzur Finances is a registered microfinance institution</p>
          <p className="text-sm mt-2">© 2024 Tzur Finances. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default WholesaleLending;
